import { useCallback, useEffect, useMemo, useState } from "react";
import {
  awarenessStageHasChildren,
  autoOpenAwarenessSingletons,
  buildAwarenessPipelineForest,
  visibleAwarenessStages,
} from "../utils/awarenessGroupTree";

function resolvePathNodes(roots, pathIds) {
  const nodes = [];
  let level = roots;
  for (const id of pathIds || []) {
    const node = (level || []).find((item) => Number(item.id) === Number(id));
    if (!node) break;
    nodes.push(node);
    level = node.children || [];
  }
  return nodes;
}

export function useAwarenessPipelineNavigation(flatStages = []) {
  const forest = useMemo(
    () => buildAwarenessPipelineForest(flatStages),
    [flatStages]
  );
  const rootKey = useMemo(
    () => forest.roots.map((node) => node.id).join(","),
    [forest.roots]
  );
  const [pathIds, setPathIds] = useState([]);

  useEffect(() => {
    setPathIds(autoOpenAwarenessSingletons(forest.roots).pathIds);
    // Só reseta o nível quando a árvore de raízes muda — não a cada refetch de totais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootKey]);

  const path = useMemo(
    () => resolvePathNodes(forest.roots, pathIds),
    [forest.roots, pathIds]
  );

  const visibleStages = useMemo(
    () => visibleAwarenessStages(forest, path),
    [forest, path]
  );

  const drillDown = useCallback(
    (stageId) => {
      const currentLevel = path.length
        ? path[path.length - 1].children || []
        : forest.roots;
      const node = currentLevel.find((item) => Number(item.id) === Number(stageId));
      if (!node || !awarenessStageHasChildren(node)) return false;

      const extra = autoOpenAwarenessSingletons(node.children).pathIds;
      setPathIds((prev) => [...prev, node.id, ...extra]);
      return true;
    },
    [forest.roots, path]
  );

  const goToRoot = useCallback(() => {
    setPathIds(autoOpenAwarenessSingletons(forest.roots).pathIds);
  }, [forest.roots]);

  const goToPathIndex = useCallback(
    (index) => {
      if (index < 0) {
        goToRoot();
        return;
      }
      setPathIds((prev) => prev.slice(0, index + 1));
    },
    [goToRoot]
  );

  return {
    visibleStages,
    path,
    breadcrumb: path,
    drillDown,
    goToRoot,
    goToPathIndex,
    hasChildren: awarenessStageHasChildren,
  };
}
